"use client";

import { useEffect, useRef, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  className?: string;
  /** Stagger delay in seconds */
  delay?: number;
  /** Rise distance in px */
  y?: number;
  /** Blur amount in px; 0 skips filter for cheaper paint */
  blur?: number;
  /** Enter duration in seconds */
  duration?: number;
  /**
   * Above-the-fold intro copy: play a pure-CSS entrance on first paint instead
   * of waiting for the scroll observer (no JS on the critical path).
   */
  enter?: boolean;
  as?: "div" | "section" | "article" | "li" | "header";
} & Omit<HTMLAttributes<HTMLElement>, "children" | "className">;

/** Elements whose top is below this fraction of the viewport start hidden. */
const FOLD = 0.92;

let sharedObserver: IntersectionObserver | null = null;

function observer(): IntersectionObserver {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute("data-revealed", "");
          sharedObserver?.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0 },
    );
  }
  return sharedObserver;
}

/**
 * Arm an element for a scroll reveal — only if it starts below the fold, so
 * server-rendered content that is already on screen never blinks out.
 * Runs from the element's own effect, i.e. strictly after it has hydrated.
 */
function register(el: HTMLElement): (() => void) | undefined {
  if (typeof IntersectionObserver === "undefined") return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  let unobserve: (() => void) | undefined;
  let raf = 0;
  let timer = 0;
  /*
   * Whether THIS registration observes the element. The data-rv-armed
   * attribute outlives a cleanup (React runs effects twice in development,
   * and a remount reuses the node), so it cannot answer that: trusting it
   * left the element armed — invisible — with nothing watching for it.
   */
  let observing = false;

  // Measure more than once. On a client navigation the scroll-to-top and the
  // first layout land after this effect, so a single reading hides content
  // that is actually on screen and the observer never gets a later chance.
  const place = () => {
    if (!el.isConnected) return;
    const box = el.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) return;
    const inView = box.top < window.innerHeight * FOLD && box.bottom > 0;
    if (inView) {
      el.setAttribute("data-revealed", "");
      el.removeAttribute("data-rv-armed");
      unobserve?.();
      unobserve = undefined;
      observing = false;
      return;
    }
    // Already shown once: never hide it again.
    if (el.hasAttribute("data-revealed")) return;
    el.setAttribute("data-rv-armed", "");
    if (observing) return;
    observing = true;
    const io = observer();
    io.observe(el);
    unobserve = () => io.unobserve(el);
  };

  place();
  raf = requestAnimationFrame(place);
  timer = window.setTimeout(place, 80);

  return () => {
    cancelAnimationFrame(raf);
    window.clearTimeout(timer);
    unobserve?.();
  };
}

/**
 * Soft rise on enter — transform/opacity only, markup always visible in the
 * HTML (crawlers, no-JS, slow networks). One IntersectionObserver is shared by
 * every Reveal on the page.
 */
export function Reveal({
  children,
  className,
  delay = 0,
  y = 28,
  blur = 0,
  duration = 0.85,
  enter = false,
  as = "div",
  style,
  ...rest
}: Props) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (enter || !ref.current) return;
    return register(ref.current);
  }, [enter]);

  const Tag = as as "div";
  const vars = {
    "--rv-delay": `${delay}s`,
    "--rv-y": `${y}px`,
    "--rv-dur": `${duration}s`,
    ...(blur > 0 ? { "--rv-blur": `${blur}px` } : {}),
    ...style,
  } as CSSProperties;

  return (
    <Tag
      {...rest}
      ref={ref as React.Ref<HTMLDivElement>}
      className={className}
      style={vars}
      data-reveal={enter ? "enter" : ""}
      data-reveal-blur={blur > 0 ? "" : undefined}
    >
      {children}
    </Tag>
  );
}
